// @givmo/app-admin — the employer portal's half of the `@givmo/step-up` seam (AS-WEB11).
//
// The portal's first "Confirm it's you": its Sign-in & security page, where adding a passkey and
// removing one meet the backend's factor rules (rule 1, rule 2). The package owns the gate and the
// sheet; this answers for the portal's Firebase session: the ways back in the account holds, the
// bound re-authentication primitives, the forced token refresh (with the suite login re-run after a
// confirmation), the words a failed re-authentication shows, the sheet's own code step completing a
// raised authenticator code, and Account Security's passkey arm — the slot filled with the portal's
// ACCOUNT-BOUND re-confirmation (`confirmWithPasskey`), never the unbound passkey sign-in. Whether the
// account holds a passkey and an authenticator comes from the server's own records, each `undefined`
// while not served, and read only when a prompt opens (AS-WEB15, `prepare`): no request on render or
// window focus. The account reset (AS-WEB13, `renderLost`) rides wherever the security read is served
// and the account has an address: the sheet's lost links open it in place.
//
// "Sign in again" (AS-PWL-2e, `signInAgain`; the cut's review, F4, as its design note listed): the
// cure for a first passkey on a sign-in older than rule 1's window, which only a NEW sign-in gives.
// The portal's sign-out releases the suite session too, so no silent handoff brings the old sign-in
// back (law 8); then a full load of THIS page, signed out, whose guard sends it to the portal's
// sign-in door with this page as the `redirect` (the charity console's host does the same).

import { useMemo } from 'react';
import { toast } from '@givmo/design-system';
import {
  AuthError,
  secondFactorChallengeOf,
  type AdminProviderId,
  type TotpChallenge,
} from '@givmo/auth';
import { sanitizeRedirectTarget } from '@givmo/org-console';
import { factorResetRenderLost, passkeyStepUpSlot, useStepUpHostFactors } from '@givmo/passkeys';
import type { StepUpHostAdapter, StepUpOwnWayIn } from '@givmo/step-up';
import type { User, UserCredential } from 'firebase/auth';
import { useAuth } from '../../auth/auth-context';
import { AUTH_ROUTES } from '../auth/auth-routing';

/**
 * Every way back in the account holds, in the order a prompt tries them: a linked password first
 * (no popup to block), then Google, then Microsoft — the portal's own sign-in set.
 */
export function adminStepUpWaysInFor(user: User | null): readonly StepUpOwnWayIn[] {
  if (user === null) return [];
  const providerIds = user.providerData.map((entry) => entry.providerId);
  const waysIn: StepUpOwnWayIn[] = [];
  if (providerIds.includes('password')) waysIn.push({ kind: 'password' });
  if (providerIds.includes('google.com'))
    waysIn.push({ kind: 'provider', wayIn: 'google', id: 'google', label: 'Google' });
  if (providerIds.includes('microsoft.com'))
    waysIn.push({ kind: 'provider', wayIn: 'microsoft', id: 'microsoft', label: 'Microsoft' });
  return waysIn;
}

/** The words a failed re-authentication shows; a cancel shows none. */
function describeAuthError(thrown: unknown): string | null {
  const authError = AuthError.from(thrown);
  if (authError.kind === 'cancelled') return null;
  return authError.message.length > 0 ? authError.message : AuthError.userMessage('unknown');
}

/** The sheet's own code step, completing the pending challenge `@givmo/auth` raised. */
const CODE_STEP: NonNullable<StepUpHostAdapter['codeStep']> = {
  resolve: async (challenge, code) => {
    await (challenge as TotpChallenge<UserCredential>).resolve(code);
  },
  isRejectedCode: (thrown) => AuthError.from(thrown).kind === 'invalidVerificationCode',
};

/**
 * Where "Sign in again" goes once signed out: back to this page, whose guard sends a signed-out
 * visitor to the portal's sign-in door with this page as its `redirect`, never a door named here. A
 * page the sanitizer refuses is never carried: the door alone.
 */
export function adminSignInAgainTarget(here: string): string {
  return sanitizeRedirectTarget(here) ?? AUTH_ROUTES.loginHome;
}

/**
 * "Sign in again": the portal's sign-out FIRST (Firebase and the suite session), then `go` back to
 * `here`, signed out, for its guard to send on to the door. A failed sign-out goes nowhere and says
 * so: reloading the page still signed in would bring the old sign-in straight back.
 */
export async function adminSignInAgainFrom(
  here: string,
  signOut: () => Promise<void>,
  go: (url: string) => void,
): Promise<void> {
  try {
    await signOut();
  } catch {
    toast.error('We couldn’t sign you out. Please try again.');
    return;
  }
  go(adminSignInAgainTarget(here));
}

/** The portal's host adapter, from its live auth context. */
export function useAdminStepUpHost(): StepUpHostAdapter {
  const {
    user,
    reauthenticateWithPassword,
    reauthenticateWithProvider,
    refreshIdToken,
    confirmWithPasskey,
    signOut,
  } = useAuth();
  const { holdsPasskey, holdsAuthenticator, security, prepare, preparing } = useStepUpHostFactors(
    user?.uid ?? null,
  );
  return useMemo<StepUpHostAdapter>(() => {
    const waysIn = adminStepUpWaysInFor(user);
    // The account reset, where it can run (AS-WEB13): both lost links open it inside the sheet,
    // and the Sign-in & security card's "Can't use a passkey?" in its own dialog. "Lost your
    // authenticator?" opens D-AS-21's B2d-lost there: a recovery code, or the emailed reset
    // (AS-WEB18), whose words say passkeys go too only where the account may hold one.
    const renderLost = factorResetRenderLost({
      email: user?.email,
      security,
      waysIn,
      holdsPasskey,
      uid: user?.uid ?? null,
      // A spent recovery code or a finished reset ends every session of the account (AS-BE63):
      // the result's Done signs out here, releasing the suite session too.
      signOut,
    });
    return {
      waysIn,
      reauthenticateWithPassword: async (password) => {
        await reauthenticateWithPassword(password);
      },
      reauthenticateWithProvider: async (providerId) => {
        await reauthenticateWithProvider(providerId as AdminProviderId);
      },
      refreshIdToken,
      secondFactorOf: (thrown) => secondFactorChallengeOf<UserCredential>(thrown),
      // The sheet draws the code step itself (`codeStep`); the portal has no step-up prompt of its own.
      renderSecondFactor: () => null,
      codeStep: CODE_STEP,
      describeError: describeAuthError,
      ...(holdsPasskey !== undefined
        ? { passkey: passkeyStepUpSlot({ holdsPasskey, reconfirm: confirmWithPasskey }) }
        : {}),
      ...(holdsAuthenticator !== undefined ? { holdsAuthenticator } : {}),
      ...(renderLost !== undefined ? { renderLost } : {}),
      // The account's facts are read when a prompt opens, not on render (AS-WEB15).
      prepare,
      preparing,
      // AS-PWL-2e — a new sign-in, back on this page (the header).
      signInAgain: () => {
        void adminSignInAgainFrom(
          `${window.location.pathname}${window.location.search}`,
          signOut,
          (url) => window.location.assign(url),
        );
      },
    };
  }, [
    user,
    reauthenticateWithPassword,
    reauthenticateWithProvider,
    refreshIdToken,
    confirmWithPasskey,
    signOut,
    holdsPasskey,
    holdsAuthenticator,
    security,
    prepare,
    preparing,
  ]);
}
